/**
 * @file projects-api.ts
 * @description Typed wrappers around the /projects/* endpoints. Customers
 *   call `createProject` + `submitProject`; everyone calls `listProjects`
 *   + `getProject` (results scoped server-side by role).
 * @module Lib
 * @security All requests go through the api wrapper which attaches the
 *   bearer token and auto-refreshes on 401.
 */

import { api } from './api';

export type ProjectStatus =
  | 'draft'
  | 'submitted'
  | 'reviewing'
  | 'quoted'
  | 'approved'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type ProjectPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface MilestoneSummary {
  total: number;
  completed: number;
  overdue: number;
}

export interface ProjectDto {
  id: string;
  customer_id: string;
  customer_email: string | null;
  title: string;
  description: string;
  status: ProjectStatus;
  priority: ProjectPriority | null;
  budget_range: string | null;
  desired_deadline: string | null;
  /**
   * Admin/staff working notes. Server returns null for customer actors.
   * Treat as private — never surface inside customer-visible UI.
   */
  internal_notes: string | null;
  created_at: string;
  updated_at: string;
  /** Slice 44 — non-null when the project is archived (hidden from
   *  default lists). The list endpoint omits archived rows unless the
   *  caller passes ?archived=true. */
  archived_at: string | null;
  row_version: number;
  /** Slice 52 — list responses only; present when the project has ≥1 milestone. */
  milestone_summary?: MilestoneSummary;
  /**
   * Per-project contact person, separate from the customer account. Admin
   * sees the object (with either field potentially null); customer-role
   * responses always receive `null` regardless of stored value.
   */
  contact: { name: string | null; email: string | null } | null;
}

export interface CreateProjectInput {
  title: string;
  description: string;
  customer_id: string;
  customer_email: string;
  customer_locale?: 'he' | 'en';
  priority?: ProjectPriority;
  budget_range?: string;
  desired_deadline?: string;
  /** Optional per-project contact name. Empty strings must be omitted. */
  contact_name?: string;
  /** Optional per-project contact email. Empty strings must be omitted. */
  contact_email?: string;
}

/**
 * Server response to POST /projects. Wraps the new project alongside the
 * auto-invite outcome — if invitation_issued is true, the customer was
 * net-new and we sent them a register link by email.
 */
export interface CreateProjectResult {
  project: ProjectDto;
  customer_email: string;
  invitation_issued: boolean;
  invitation_id?: string;
  invitation_email_delivered?: boolean;
  dev_register_url?: string;
}

export interface ListProjectsResponse {
  projects: ProjectDto[];
}

export function listProjects(
  opts: { customerId?: string; includeArchived?: boolean } = {},
): Promise<ListProjectsResponse> {
  const params = new URLSearchParams();
  if (opts.customerId !== undefined) params.set('customer_id', opts.customerId);
  if (opts.includeArchived === true) params.set('archived', 'true');
  const qs = params.toString();
  return api<ListProjectsResponse>(`/projects${qs.length === 0 ? '' : `?${qs}`}`, {
    method: 'GET',
  });
}

export function getProject(id: string): Promise<ProjectDto> {
  return api<ProjectDto>(`/projects/${encodeURIComponent(id)}`, { method: 'GET' });
}

export function createProject(input: CreateProjectInput): Promise<CreateProjectResult> {
  return api<CreateProjectResult>('/projects', { method: 'POST', json: input });
}

export function submitProject(id: string): Promise<ProjectDto> {
  return api<ProjectDto>(`/projects/${encodeURIComponent(id)}/submit`, { method: 'POST' });
}

export interface UpdateProjectInput {
  row_version: number;
  title?: string;
  description?: string;
  priority?: ProjectPriority | null;
  budget_range?: string | null;
  desired_deadline?: string | null;
  /** Pass empty string or null to clear the stored contact name. */
  contact_name?: string | null;
  /** Pass empty string or null to clear the stored contact email. */
  contact_email?: string | null;
}

export function updateProject(id: string, input: UpdateProjectInput): Promise<ProjectDto> {
  return api<ProjectDto>(`/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: input,
  });
}

export function transitionProject(id: string, to: ProjectStatus): Promise<ProjectDto> {
  return api<ProjectDto>(`/projects/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    json: { to },
  });
}

/**
 * Admin/staff only. The server returns 403 to customers; the UI hides
 * the control entirely. Soft-delete keeps the row for audit recovery.
 */
export function deleteProject(id: string): Promise<void> {
  return api<void>(`/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Customer-only cancel — works from draft/submitted/quoted statuses.
 * Server rejects non-customers with 403 and other statuses with 409.
 * Admin gets emailed.
 */
export function cancelOwnProject(id: string): Promise<void> {
  return api<void>(`/projects/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
}

export interface UpdateProjectNotesResponse {
  id: string;
  internal_notes: string | null;
  updated_at: string;
  row_version: number;
}

/**
 * Admin/staff only. The server returns 403 to customers. Pass `null` or
 * empty string to clear. Max 5000 characters.
 */
export function updateProjectNotes(
  id: string,
  notes: string | null,
): Promise<UpdateProjectNotesResponse> {
  return api<UpdateProjectNotesResponse>(`/projects/${encodeURIComponent(id)}/notes`, {
    method: 'PATCH',
    json: { notes },
  });
}

/** Slice 44 — admin or owning customer. Idempotent (no-op if already
 *  in target state). Returns void; caller refetches the project. */
export function archiveProject(id: string): Promise<void> {
  return api<void>(`/projects/${encodeURIComponent(id)}/archive`, { method: 'POST', json: {} });
}

export function unarchiveProject(id: string): Promise<void> {
  return api<void>(`/projects/${encodeURIComponent(id)}/unarchive`, { method: 'POST', json: {} });
}

export interface ProjectStatusEvent {
  status: ProjectStatus;
  at: string;
}

/** Slice 56 — derived from audit_log; one entry per lifecycle status the
 *  project has been in, oldest-first starting with 'draft' at creation. */
export function getProjectStatusHistory(id: string): Promise<{ history: ProjectStatusEvent[] }> {
  return api<{ history: ProjectStatusEvent[] }>(
    `/projects/${encodeURIComponent(id)}/status-history`,
  );
}

export interface InviteContactResult {
  invited: boolean;
  alreadyMember: boolean;
  userExisted: boolean;
}

/** Slice 98 — admin-only. Retroactively invite (or directly add) the
 *  given email as a member of this project. Idempotent: re-calling on an
 *  already-member contact returns alreadyMember: true. */
export function inviteProjectContact(
  id: string,
  body: { email: string; name?: string | null },
): Promise<InviteContactResult> {
  return api<InviteContactResult>(`/admin/projects/${encodeURIComponent(id)}/invite-contact`, {
    method: 'POST',
    json: body,
  });
}

/**
 * Slice 99 — single row in the project members list.
 * - `status: 'active'` rows always have `user_id`. `invitation_id` is null.
 * - `status: 'pending_registration'` rows have `invitation_id` and a null
 *   `user_id` — the contact was invited but hasn't completed registration.
 * The UI decides which DELETE endpoint to call based on `status`.
 */
export interface ProjectMember {
  user_id: string | null;
  invitation_id: string | null;
  email: string;
  full_name: string | null;
  status: 'active' | 'pending_registration';
  granted_at: string;
}

export interface ListProjectMembersResponse {
  members: ProjectMember[];
}

/** Slice 99 — admin-only. Lists active members + pending-registration
 *  invitees for the project, oldest grant first. */
export function listProjectMembers(id: string): Promise<ListProjectMembersResponse> {
  return api<ListProjectMembersResponse>(`/admin/projects/${encodeURIComponent(id)}/members`, {
    method: 'GET',
  });
}

/** Slice 99 — admin-only. Revokes an active member's access to the
 *  project. The user account itself is untouched; only the project grant
 *  is removed. 204 on success. */
export function removeProjectMember(projectId: string, userId: string): Promise<void> {
  return api<void>(
    `/admin/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

/** Slice 99 — admin-only. Cancels a pending-registration invitation
 *  for this project (used for rows with status === 'pending_registration'
 *  that have no user_id yet). 204 on success. */
export function removeProjectInvitation(projectId: string, invitationId: string): Promise<void> {
  return api<void>(
    `/admin/projects/${encodeURIComponent(projectId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE' },
  );
}
